/**
 * AI 트래커 산점도 '일별 재생' 타임라인 캐시 (sessionStorage).
 *
 * 재생 원리: /api/genetics/engine 은 결정적 순차 시뮬레이션이라 end_date 를 날짜별로 밀면
 * 각 시점의 개념 좌표(r/h/stability, 717개)를 재현한다. 프레임 = 특정 KST 날짜까지의 엔진 상태.
 * 프레임은 GET /api/genetics/engine/timeline 한 번으로 받는다(BE-20260915 회신 — 서버가 한 번 적용하며 날마다 찍는다,
 * 날짜 D 프레임 = /engine?end_date=D). 날짜마다 end_date 를 밀어 부르는 옛 방식은 그 조회가 안 될 때만 쓴다.
 *
 * 캐시 키에 endDate 를 포함(design v1): 프레임은 [startDate, 그 날] 윈도우로 생성하므로 필터
 * endDate 가 프레임 집합을 결정한다. endDate 무관 전역 프레임(+재생 슬라이스)은 지문 무효화가
 * 미묘해져 v1 에선 채택하지 않는다(윈도우별 near-dup 저장은 quota 축출로 상쇄).
 *
 * 지문(fingerprint)은 라이브 엔진 응답 기준 — /students 는 tree 필터('진도와 과제'·미마감 제외)로
 * 엔진(전체 stamp)과 시험 집합이 달라 stale 캐시를 유효 판정할 수 있다(교차검증 확인).
 */

const PREFIX = 'aitracker:timeline:v1:';
const VERSION = 1;

export function timelineKey(studentId, startDate, endDate) {
  return `${PREFIX}${studentId}|${startDate || ''}|${endDate || ''}`;
}

// 라이브 엔진 응답의 (K 버전 · 시험 id 순열 · 마지막 시험 갱신시각)으로 데이터 변화를 감지.
// K 교체는 717 좌표 전부를 바꾸므로 반드시 포함. 시험 추가·삭제·재마감(updated_at 갱신)도 잡힌다.
export function fingerprintOf(engineData) {
  if (!engineData) return null;
  const kSeq = engineData.k && engineData.k.seq != null ? engineData.k.seq : '';
  const examIds = Array.isArray(engineData.exam_ids) ? engineData.exam_ids.join(',') : '';
  const last = (engineData.delta && engineData.delta.exam && engineData.delta.exam.updated_at) || '';
  return `${kSeq}|${examIds}|${last}`;
}

// updated_at 은 타임존 표기 없는 naive UTC 문자열('2026-05-27T12:39:38.75') → 반드시 UTC 로 확정한 뒤
// +9h 로 KST 벽시계 날짜를 뽑는다. new Date(문자열)의 브라우저 로컬 해석에 의존하지 않는다.
export function kstDateOf(iso) {
  if (!iso) return null;
  const hasTz = /[zZ]$/.test(iso) || /[+-]\d\d:?\d\d$/.test(iso);
  const t = Date.parse(hasTz ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return null;
  return new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

// 엔진 응답 → 슬림 프레임(양자화 정수 배열 3종만). last_record/total_record/avg_diff/engine_params 는
// 차트·Δ 렌더에 불필요 → 저장 안 함(프레임당 용량 절반). Δ는 재생 시 프레임 간 client diff 로 산출.
export function extractFrame(engineData) {
  return {
    r: engineData.r_status || [],
    h: engineData.h_status || [],
    s: engineData.stability || [],
  };
}

// 엔진 초기 baseline 중 stability 값(양자화 정수). 관측이 없는 개념은 (r=0, h=0, st=0.5)에서 출발한다.
export function stBaseline(vScale) {
  return Math.trunc(0.5 * (vScale || 10000));
}

// '초기 디폴트' 판정 — 3축이 모두 baseline 그대로 = 학습 기록이 한 번도 반영되지 않은 개념.
// decodeQ 이전의 양자화 정수로 비교해 부동소수 오차를 배제한다(0.4999 가 '0.50'으로 보이는 표시값 비교 금지).
// 한 축이라도 움직였으면 디폴트가 아니다. 값이 없으면(undefined) 디폴트로 보지 않는다 — 숨김은 확실할 때만.
export function isEngineDefault(r, h, s, vScale) {
  return r === 0 && h === 0 && s === stBaseline(vScale);
}

// 타임라인 응답(GET /api/genetics/engine/timeline)의 지문 — fingerprintOf(라이브 /engine)와 같은 모양이라 둘을 바로 견준다.
// 회신이 세 값을 최상위에 준다: k · exam_ids · last_exam_updated_at(= /engine 의 delta.exam.updated_at 과 같은 값).
export function timelineFingerprintOf(tl) {
  if (!tl) return null;
  const kSeq = tl.k && tl.k.seq != null ? tl.k.seq : '';
  const examIds = Array.isArray(tl.exam_ids) ? tl.exam_ids.join(',') : '';
  return `${kSeq}|${examIds}|${tl.last_exam_updated_at || ''}`;
}

// 타임라인 응답 → 재생 날짜별 슬림 프레임 { 'yyyy-mm-dd': {r,h,s} }.
// 서버 days 는 엔진이 적용한 모든 시험의 날(v2 시험지 포함)이고 재생 날짜는 /genetics/students 시험의 날이라 서로 다를 수 있다.
// 날짜 D 의 프레임 = D 이하 서버 날 중 가장 늦은 날의 상태. 서버 프레임이 「그날 마지막 시험 적용 직후」라 이것이 곧
// /engine?end_date=D 다(날짜마다 부르던 옛 방식과 같은 값). D 까지 적용된 시험이 없으면 엔진 초기 상태(r 0 · h 0 · st 0.5).
// 모양이 어긋나면 null — 호출부는 날짜별 호출로 넘어간다.
export function framesForDates(tl, dates) {
  if (!tl || !Array.isArray(tl.days) || !Array.isArray(tl.frames) || tl.days.length !== tl.frames.length) return null;
  const n = Array.isArray(tl.concept_index) ? tl.concept_index.length : 0;
  const initial = { r: new Array(n).fill(0), h: new Array(n).fill(0), s: new Array(n).fill(stBaseline(tl.v_scale)) };
  const out = {};
  dates.forEach((d) => {
    // days 는 오름차순·중복 없음(회신 §1) — D 이하인 마지막 칸을 이분 탐색
    let lo = 0;
    let hi = tl.days.length - 1;
    let at = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (tl.days[mid] <= d) { at = mid; lo = mid + 1; } else hi = mid - 1;
    }
    out[d] = at >= 0 ? tl.frames[at] : initial;
  });
  return out;
}

// 프레임 간 Δ(client diff): "그 날 하루 전체 변화" = frame - prev. 첫 프레임은 엔진 초기 baseline
// (r=0, h=0, st=0.5) 대비. 양자화 단위 그대로(정수) → engineTableRows 가 decodeQ 로 나눈다.
export function frameDelta(frame, prev, vScale) {
  const n = frame.r.length;
  const halfSt = stBaseline(vScale);
  const dr = new Array(n);
  const dh = new Array(n);
  const ds = new Array(n);
  for (let i = 0; i < n; i += 1) {
    dr[i] = frame.r[i] - (prev ? prev.r[i] : 0);
    dh[i] = frame.h[i] - (prev ? prev.h[i] : 0);
    ds[i] = frame.s[i] - (prev ? prev.s[i] : halfSt);
  }
  return { delta_r_status: dr, delta_h_status: dh, delta_stability: ds };
}

// 재생 중 축·크기 동결값 = 전 프레임 union(현재 delta/Y:st 모드 반영). plotX=r|dr, plotY=(stabY?st:h)|delta,
// plotZ=st|dst. 데이터 한계만 산출(gutter 확장은 호출부 axisRange 메모가 현재 chartDimensions로 반응형 적용).
// hideDefaults: 프레임마다 초기 디폴트인 개념을 union에서 제외 — 화면 필터와 같은 규칙(프레임 기준)이라
// 동결 눈금이 '실제로 그려지는 점'의 범위와 일치한다. union 자체는 1회 계산이라 동결의 의미는 그대로다.
// (축 lim은 0 대칭이라 (0,0) 제외 여부와 무관하나, z 범위는 빼지 않으면 0.5에 끌려간다.)
// 남는 점이 하나도 없으면 null — 호출부는 동결 없이 화면 데이터 기준으로 폴백한다.
export function computeFrozenBounds(env, deltaMode, stabY, hideDefaults) {
  if (!env || !Array.isArray(env.dates) || !env.dates.length) return null;
  const vScale = env.vScale || 10000;
  const halfSt = stBaseline(vScale);
  let xMin = Infinity; let xMax = -Infinity;
  let yMin = Infinity; let yMax = -Infinity;
  let zMin = Infinity; let zMax = -Infinity;
  for (let di = 0; di < env.dates.length; di += 1) {
    const f = env.frames[env.dates[di]];
    if (!f) continue;
    const prev = di > 0 ? env.frames[env.dates[di - 1]] : null;
    for (let i = 0; i < f.r.length; i += 1) {
      // 판정은 delta 모드와 무관하게 원본 값 기준 — 화면 필터(defaultCidSet)와 동일한 규칙.
      if (hideDefaults && isEngineDefault(f.r[i], f.h[i], f.s[i], vScale)) continue;
      const r = deltaMode ? (f.r[i] - (prev ? prev.r[i] : 0)) / vScale : f.r[i] / vScale;
      const h = deltaMode ? (f.h[i] - (prev ? prev.h[i] : 0)) / vScale : f.h[i] / vScale;
      const s = deltaMode ? (f.s[i] - (prev ? prev.s[i] : halfSt)) / vScale : f.s[i] / vScale;
      const py = stabY ? s : h;
      if (r < xMin) xMin = r; if (r > xMax) xMax = r;
      if (py < yMin) yMin = py; if (py > yMax) yMax = py;
      if (s < zMin) zMin = s; if (s > zMax) zMax = s;
    }
  }
  if (!Number.isFinite(xMin)) return null;
  const rawXLim = Math.max(Math.abs(Math.floor(xMin * 10) / 10), Math.abs(Math.ceil(xMax * 10) / 10), 0.1);
  const rawYLim = Math.max(Math.abs(Math.floor(yMin * 10) / 10), Math.abs(Math.ceil(yMax * 10) / 10), 0.1);
  return {
    rawXLim, rawYLim,
    sizeRange: { min: zMin, max: zMax },
    stabilityBounds: { min: Math.floor(zMin * 100) / 100, max: Math.ceil(zMax * 100) / 100 },
  };
}

export function loadEnvelope(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const env = JSON.parse(raw);
    return env && env.v === VERSION ? env : null;
  } catch (_) {
    return null;
  }
}

// 저장 실패(Quota·Safari 프라이빗 등)는 치명적이지 않다 — 타 학생 타임라인 키를 축출하고 1회 재시도,
// 그래도 실패하면 메모리 전용(false 반환, 호출부는 계속 재생 가능).
export function saveEnvelope(key, env) {
  const payload = JSON.stringify(env);
  try {
    sessionStorage.setItem(key, payload);
    return true;
  } catch (_) {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(PREFIX) && k !== key) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(key, payload);
      return true;
    } catch (__) {
      return false;
    }
  }
}

export const TIMELINE_VERSION = VERSION;
